/**
 * RESPONSIBILITY: what an annotation records. Types only, no logic, no registry.
 *
 * Legacy TypeScript decorators (`experimentalDecorators: true`). esbuild supports
 * these but NOT `emitDecoratorMetadata`, so a field's declared TYPE cannot be
 * reflected. Everything downstream follows from that one constraint:
 *
 *   · The annotation carries the type — `@Color`, `@Img`, `@DateField`. Say nothing
 *     about the type (`@Field()`) and you get text, the default.
 *   · The property NAME is always available, so `label` is optional everywhere and
 *     falls back to the humanized field name (`blueDeep` → "Blue Deep").
 *   · A field with NO annotation is invisible to the framework. That is the one real
 *     cost of not having decorator metadata.
 */
import type { Audience } from '../index';

export type FieldKind =
  | 'text' | 'paragraph' | 'color' | 'img' | 'video' | 'date'
  | 'num' | 'bool' | 'choice' | 'list' | 'link' | 'stringlist' | 'repeat';

/**
 * A field that is only asked for when a SIBLING field holds one of these values.
 *
 * The named field is looked up in the same object — the same row for a column, the
 * document root for a top-level field. That is the whole of the language on purpose:
 * a condition that can reach across the document is one an editor cannot predict from
 * what is in front of them, and one this file would have to teach four generators.
 *
 * PRESENTATION ONLY. A hidden field is not a forbidden one — the schema, the validator
 * and what is stored are identical either way, so turning a condition on or off never
 * needs a migration and never strands a value that was already saved.
 */
export interface ShowWhen {
  /** Sibling field key. */
  field: string;
  /** Shown when that field holds one of these. */
  is: readonly string[];
}

export interface FieldMeta {
  key: string;
  kind: FieldKind;
  label: string;
  description?: string;
  required: boolean;
  order: number;
  default?: unknown;

  /**
   * The server owns this value. It is stripped from every write before validation and
   * refilled by the section's own prepare(), so no request can set it and no control
   * renders it. See core/write-pipeline.ts.
   */
  derived?: boolean;

  /**
   * Panel this field is edited in. Fields naming the same group are collected into one
   * collapsible section of the form, in the order the first of them was declared.
   * Omitted ⇒ the field sits at the top level, as everything did before.
   */
  group?: string;

  /**
   * Start that group CLOSED. Declared on the first field of the group, because that is
   * the one that creates it. A long row is readable only if the parts nobody needs are
   * out of the way until they are.
   */
  collapsed?: boolean;

  /**
   * Only ask for this field when a sibling holds one of the stated values. See ShowWhen.
   */
  showWhen?: ShowWhen;

  /**
   * This field holds the rendered form of the markdown field it names.
   *
   * Machine-written and ALWAYS overwritten on save, which is what makes it safe to put
   * through dangerouslySetInnerHTML: whatever a client sends is discarded rather than
   * defended against. That is one rule instead of two, and the one that cannot be got
   * wrong. Validated at boot — a name with no markdown field behind it throws.
   */
  renderOf?: string;

  // text / paragraph / link
  maxLength?: number;
  minLength?: number;
  rows?: number;
  /**
   * The value is markdown, and the page renders it with <Markdown>. Storage does not
   * change — what is saved is the raw text the editor typed, which is why turning this
   * on later needs no migration and why the document stays readable.
   */
  markdown?: boolean;
  /**
   * Directive names this field may use — `['verse', 'callout']` permits `:verse[…]` and
   * `:::callout`. Each renders with the class `md-<name>`, derived rather than mapped,
   * so allowing one is the whole of what an app does before styling `.md-verse`.
   *
   * Enforced on write: a name not listed is refused, rather than silently rendering as
   * text the editor cannot see the reason for.
   */
  allow?: readonly string[];

  // num
  min?: number;
  max?: number;
  integer?: boolean;

  // choice
  /** Fixed vocabulary, known at compile time. Mutually exclusive with `in`. */
  options?: readonly string[];
  /**
   * Where the allowed values live in THIS section's document — `categories` for a field
   * at the root, `./kinds` for one on the same row. Resolved when the control renders
   * and when validate() runs, never when the schema is compiled, which is what keeps
   * compile() a pure function of the annotations.
   */
  in?: string;
  /** Holds several of the allowed values rather than one. */
  multiple?: boolean;
  /**
   * Draw the choice as native inputs rather than the default chrome — radios instead of
   * a `<select>`, checkboxes instead of toggle chips.
   *
   * PRESENTATION ONLY. The stored value, the JSON Schema and what validate() accepts are
   * identical either way, so this can be turned on or off without a migration. It exists
   * because a `<select>` hides its options behind a click, and four of them a visitor
   * must weigh up should be four things they can see at once.
   */
  as?: 'radio' | 'checkbox';

  // img and video — the same three, because they are the same upload
  folder?: string;
  maxSizeMb?: number;
  /**
   * The pixel size this slot wants, shown on the control.
   *
   * Advice, not a rule: nothing rejects an image for being the wrong size, because the
   * page crops and scales whatever it is given and an editor blocked at five to ten on a
   * Sunday has no way round it. What it removes is the guessing — a picture chosen for a
   * band that is three times as wide as it is tall should be that shape before it is
   * uploaded, not discovered to be wrong once it is on the page.
   */
  size?: string;

  // date
  time?: boolean;

  // list
  of?: () => Function;
  minItems?: number;
  maxItems?: number;
}

export interface SectionMeta {
  name: string;
  label: string;
  ctor: Function;
  fields: FieldMeta[];
  /** Who may READ. Defaults to [ALL] — anyone, including anonymous visitors. */
  viewableBy: Audience;
  /** Who may WRITE. Defaults to [NONE] — read-only until access is granted. */
  editableBy: Audience;
}

export interface SectionOptions {
  name: string;
  label: string;
  /**
   * This section is part of the site's frame, not of its content, so it carries no
   * visibility switch.
   *
   * Everything else gets one — see HIDDEN. The navigation is the exception because a
   * hidden header is a site with no way to reach any other page, including the page
   * somebody would go to in order to unhide it. Marking it here rather than teaching
   * <SectionUpdate> a list of names keeps the fact with the section that owns it.
   */
  always?: boolean;
}

/**
 * The key every @List row carries, so a row can be referred to at all.
 *
 * Position is not identity: a link to `#event/2` points at a different event the moment
 * somebody reorders the list, silently. This is generated when a row is added, never
 * edited, never rendered, and never reused — the same reasoning that took image
 * filenames off row index.
 */
export const ROW_ID = '_rid';

/**
 * The key every SECTION carries, whether or not its class declared anything.
 *
 * Hiding a band is a content decision — "not this month" — and content decisions belong
 * to the person editing, not to a deploy. Every section gets it, so there is no list of
 * which sections can be hidden to keep in step with the sections that exist, and so a
 * section added tomorrow arrives with it already.
 *
 * Added by the registry rather than by each class: one edit means every section, and a
 * section that forgot to declare it would be the only one an editor could not switch off.
 */
export const HIDDEN = 'hidden';

/** Options every field annotation accepts. */
export interface CommonFieldOptions {
  label?: string;
  description?: string;
  required?: boolean;
  /** Server-owned. Never accepted from a client, never rendered. See FieldMeta.derived. */
  derived?: boolean;
  /** Collapsible panel to edit this field in. See FieldMeta.group. */
  group?: string;
  /** That panel starts closed. See FieldMeta.collapsed. */
  collapsed?: boolean;
  /** Ask for this field only when a sibling field holds one of these values. */
  showWhen?: ShowWhen;
}
